import React, { useState, useEffect, useCallback } from "react";
import CommonSelect from "../../../../core/common/commonSelect";
import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import { notify } from "../../../../services/toast.config";

// Types
interface AddSubCategoryProps {
  selectedCompany: {
    company_id: string;
    company_name: string;
    company_type: string;
  };
  parentCategory?: any;
  updateUI: () => Promise<void>;
}

type SubCategoryRow = {
  name: string;
  status: string;
};

type SubCategoryFormData = {
  parent_category_id: string | any;
  parent_category_name: string | any;
  company_id: string | any;
  company_type: string | any;
  company_name: string | any;
  rows: SubCategoryRow[];
};

const DEFAULT_ROW: SubCategoryRow = {
  name: "",
  status: "Active",
};

const DEFAULT_FORM_DATA: SubCategoryFormData = {
  parent_category_id: null,
  parent_category_name: "",
  company_id: null,
  company_type: null,
  company_name: null,
  rows: [{ ...DEFAULT_ROW }],
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const AddSubCategory: React.FC<AddSubCategoryProps> = ({
  selectedCompany,
  parentCategory,
  updateUI,
}) => {
  // Local Storage
  const signature = localStorage.getItem("signature");

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string[];
  }>({});
  const [formData, setFormData] =
    useState<SubCategoryFormData>(DEFAULT_FORM_DATA);

  // Helper Functions
  const resetForm = useCallback(() => {
    const { company_id, company_name, company_type } = selectedCompany;
    const { parent_category_id, parent_category_name } = parentCategory;
    setFormData({
      parent_category_id,
      parent_category_name,
      company_id,
      company_name,
      company_type,
      rows: [{ ...DEFAULT_ROW }],
    });
    setInvalidFields(new Set());
    setValidationErrors({});
  }, [selectedCompany, parentCategory]);

  const validateForm = useCallback((): boolean => {
    const errors: { [key: string]: string[] } = {};
    const invalid = new Set<string>();

    if (!formData.parent_category_id) {
      errors["parent_category_id"] = ["Parent Category is required"];
      invalid.add("parent_category_id");
    }
    // Validate each row
    formData.rows.forEach((row, index) => {
      const rowErrors: string[] = [];

      if (!row.name.trim()) {
        rowErrors.push("Sub-category name is required");
      }

      if (!row.status) {
        rowErrors.push("Status is required");
      }

      const duplicateIndex = formData.rows.findIndex(
        (r, i) =>
          i !== index &&
          r.name.trim().toLowerCase() === row.name.trim().toLowerCase()
      );
      if (duplicateIndex !== -1) {
        rowErrors.push("Duplicate sub-category name");
      }

      if (rowErrors.length > 0) {
        errors[`row_${index}`] = rowErrors;
        invalid.add(`row_${index}`);
      }
    });
    const validRows = formData.rows.filter((row) => row.name.trim());
    if (validRows.length === 0) {
      errors["rows"] = ["Add at least one sub-category"];
      invalid.add("rows");
    }
    setValidationErrors(errors);
    setInvalidFields(invalid);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const clearFieldError = useCallback((fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });

    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  }, []);

  const addRow = () => {
    setFormData((prev) => ({
      ...prev,
      rows: [...prev.rows, { ...DEFAULT_ROW }],
    }));
  };

  const removeRow = (index: number) => {
    if (formData.rows.length > 1) {
      clearFieldError(`row_${index}`);
      setFormData((prev) => ({
        ...prev,
        rows: prev.rows.filter((_, i) => i !== index),
      }));
    }
  };

  const updateRow = (
    index: number,
    field: keyof SubCategoryRow,
    value: string
  ) => {
    clearFieldError(`row_${index}`);
    setFormData((prev) => ({
      ...prev,
      rows: prev.rows.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const handleSubmitSubCategories = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      setIsLoading(true);

      const validRows = formData.rows.filter((row) => row.name.trim());

      const subcategories = validRows.map((row) => ({
        name: row.name.trim(),
        status: row.status.toLowerCase() === "active" ? 1 : 0,
      }));

      const payload = {
        signature,
        company_id: formData.company_id,
        company_type: formData.company_type,
        parent_category_id: formData.parent_category_id,
        subcategories,
        category_level: 1,
      };
      const response = await api.post("userService/addSubCategories", payload);
      resetForm();
      notify.success(
        response.data?.message[0]?.msg ||
          `${validRows.length} sub-categories created successfully under "${formData.parent_category_name}".`
      );
      // Close modal
      const dismissButton = document.querySelector(
        "#dismiss-sub-category-modal-btn"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      await updateUI();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.data?.message?.[0]?.msg ||
        error?.data?.message ||
        "Internal Server Error!";
      notify.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Initialize form data
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  return (
    <>
      {isLoading && <Loader />}

      <div className="modal fade" id="add_sub_category_modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Sub Categories</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>

            <form onSubmit={handleSubmitSubCategories}>
              <div
                className="modal-body pb-0"
                style={{
                  minHeight: "300px",
                  maxHeight: "450px",
                  overflowY: "auto",
                }}>
                <div className="row">
                  {/* Company & Parent Category */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className="select"
                      options={[]}
                      defaultValue={{
                        value: formData.company_id,
                        label: `${formData.company_name} - ${formData.company_type}`,
                      }}
                      isDisabled={true}
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Main Category <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFields.has("parent_category_id")
                          ? "is-invalid border border-danger rounded"
                          : ""
                      }>
                      <CommonSelect
                        options={[]}
                        defaultValue={{
                          value: formData.parent_category_id,
                          label: formData.parent_category_name,
                        }}
                        isDisabled={true}
                      />
                    </div>
                    {validationErrors["parent_category_id"] && (
                      <div className="text-danger mt-1">
                        <small>
                          {validationErrors["parent_category_id"][0]}
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Sub Category Rows */}
                  <div className="col-md-12 mb-3">
                    <div className="row mb-2">
                      <div className="col-md-6">
                        <label className="form-label">
                          Sub-Category Name{" "}
                          <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">
                          Status <span className="text-danger">*</span>
                        </label>
                      </div>
                    </div>

                    {/* Rows */}
                    {formData.rows.map((row, index) => (
                      <div key={index} className="row mb-2 align-items-start">
                        {/* Name Input */}
                        <div className="col-md-6">
                          <input
                            type="text"
                            className={`form-control ${
                              validationErrors[`row_${index}`]
                                ? "is-invalid border-danger"
                                : ""
                            }`}
                            value={row.name}
                            onChange={(e) =>
                              updateRow(index, "name", e.target.value)
                            }
                            placeholder={`Sub-category ${index + 1} name`}
                            disabled={isSubmitting}
                          />
                        </div>

                        {/* Status Toggle */}
                        <div className="col-md-3 d-flex align-items-center">
                          <div className="form-check form-switch d-flex align-items-center gap-2 py-2">
                            <input
                              type="checkbox"
                              className="form-check-input "
                              style={{ width: "2rem", height: "1.2rem" }}
                              checked={row.status === "Active"}
                              disabled={isSubmitting}
                              onChange={(e) =>
                                updateRow(
                                  index,
                                  "status",
                                  e.target.checked ? "Active" : "Inactive"
                                )
                              }
                            />
                            <label className="fs-14 fw-medium mb-0">
                              {row.status === "Active" ? "Active" : "Inactive"}
                            </label>
                          </div>
                        </div>

                        {/* Remove Button */}
                        <div className="col-md-2">
                          <button
                            type="button"
                            className="btn btn-md btn-outline-danger py-2"
                            onClick={() => removeRow(index)}
                            disabled={
                              formData.rows.length === 1 || isSubmitting
                            }
                            title="Remove sub category">
                            <i className="ti ti-trash me-1"></i>
                            Remove
                          </button>
                        </div>

                        {/* Row Errors */}
                        {validationErrors[`row_${index}`] && (
                          <div className="col-md-12 mt-1">
                            <div className="text-danger">
                              <small>
                                {validationErrors[`row_${index}`].map(
                                  (error, errorIndex) => (
                                    <div key={errorIndex}>• {error}</div>
                                  )
                                )}
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <button
                      type="button"
                      className="btn btn-md btn-outline-secondary "
                      onClick={addRow}
                      disabled={isSubmitting}>
                      <i className="ti ti-plus me-1"></i>
                      Add More Category
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Creating...
                    </>
                  ) : (
                    `Create Sub-Categories`
                  )}
                </button>

                <button
                  id="dismiss-sub-category-modal-btn"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddSubCategory;
